/**
 * Menu controller.
 *
 * This class controls the site menu and search functions.
 */
import Autocomplete from './autocomplete';

// Use jQuery as $ within this file scope.
const $ = jQuery;

// Use lodash
import _ from 'lodash';

/**
 * Export the class reference.
 */
export default class Menu {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {}

    /**
     * Cache dom elements for use in the class's methods
     */
    cache() {

        // The mobile menu hamburger icon.
        this.hamburger = document.getElementById( 'js-hamburger' );

        // The mobile full screen overlay that contains the mobile menu.
        this.mobileOverlay = document.getElementById( 'js-mobile-overlay' );

        // The mobile nav element.
        this.mobileMenu = document.getElementById( 'js-nav--mobile' );

        // Get first and last focusable elements of the mobile menu.
        if ( this.mobileMenu ) {

            const focusableElements = this.mobileMenu.querySelectorAll( 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]' );
            if ( focusableElements.length ) {
                const focusableElementsArr = Array.prototype.slice.call( focusableElements );

                this.firstMobileMenuLink = focusableElementsArr[0];
                this.lastMobileMenuLink = focusableElementsArr[focusableElementsArr.length - 1];
            }
        }

        // The toggler button for the desktop navigation's search form container.
        this.searchFormToggler = document.getElementById( 'js-searchform-toggle' );

        // The desktop navigation's search form.
        this.searchForm = document.getElementById( 'js-nav--desktop__search__form' );

        // The text input area inside the searchFormContainer
        this.searchFormDesktopInput = document.getElementById( 'nav--desktop__search__form__input' );

        // The container for the jQuery UI autocompletion.
        this.searchAutocompleteContainer = document.getElementById( 'js-nav--desktop__search__form__autocomplete' );

        this.autocompleteResultsContainer = document.getElementById( 'js-search-result-container' );

        // Each top level menu item that has children.
        this.menuParents = Theme.Common.$( '.js-main-menu-nav .menu-item-has-children' );

        // Each submenu inside the top level menu items that have children.
        if ( this.menuParents.length ) {
            this.menuSubMenus = Theme.Common.childrenFromEach( this.menuParents, '.sub-menu' );
        }

        // Each top level menu item that has children.
        this.langMenuParents = Theme.Common.$( '.js-main-menu-lang .menu-item-has-children' );

        // Each submenu inside the top level menu items that have children.
        if ( this.langMenuParents.length ) {
            this.langMenuSubMenus = Theme.Common.childrenFromEach( this.langMenuParents, '.sub-menu' );
        }

        // Dark overlay for search.
        this.darkOverlay = document.getElementById( 'js-search-dark-overlay' );
    }

    /**
     * All common events
     * @return {void}
     */
    events() {
        document.addEventListener( 'keydown', evt => this.keyDownHandler( evt ) );
        document.addEventListener( 'keydown', evt => this.searchOverlayKeyDownControl( evt ) );

        // Prevent search if input value is under three characters.
        this.searchForm.addEventListener( 'submit', evt => {
            if ( this.searchFormDesktopInput.value.length < 3 ) {
                evt.preventDefault();
            }
        });

        // On focus events for search fields.
        this.searchFormDesktopInput.addEventListener( 'focus', evt => this.searchActive( evt ) );

        // On input change, change search as active if not already and enable/disable search button.
        this.searchFormDesktopInput.addEventListener( 'input', evt => this.maybeActivateSearch( evt ) );

        document.addEventListener( 'click', evt => this.maybeCloseMenu( evt ) );

        // Click to close navigation menus.
        document.addEventListener( 'click', evt => this.closeNavigationMenus( evt ) );
    }

    maybeCloseMenu( event ) {
        if ( this.currentSubMenu ) {
            if ( ! this.currentSubMenu.contains( event.target ) ) {
                this.closeAllNavMenus();
            }
        }

    }

    /**
     * Check if the search is active.
     *
     * @returns {boolean}
     */
    isSearchOpen() {
        return this.darkOverlay.classList.contains( 'search-active' );
    }

    /**
     * Submits the search form. Used with html data attribute triggers.
     */
    submitSearch() {
        if ( this.searchFormDesktopInput.value.length >= 3 ) {
            this.searchForm.submit();
        }
    }

    /**
     * Displays the autocompletion container.
     */
    displayAutocomplete() {
        this.searchAutocompleteContainer.classList.add( 'js-active' );
    }

    /**
     * Hides the autocompletion container.
     */
    hideAutocomplete() {
        this.searchAutocompleteContainer.classList.remove( 'js-active' );
    }

    /**
     * Controls the autocompletion functionality for the search form.
     */
    searchAutocomplete() {

        // Initialize the autocompletion instance.
        new Autocomplete(
            this.searchFormDesktopInput, // The desktop input element
            'Header', // Header is the DP model.
            'get_autocomplete_suggestions', // This is the AJAX method.
            'search-autocomplete',
            this.autocompleteResultsContainer, // Here we add the resulted HTML.
            ( content ) => {
                if ( content ) {
                    this.displayAutocomplete(); // On success.
                } else {
                    this.hideAutocomplete(); // Hide on empty result.
                }
            },
            ( error ) => {
                this.hideAutocomplete(); // On error.
                console.log( error );
            },

            // Before XHR requests.
            () => {
                $( this.autocompleteResultsContainer ).html( '<em class="nav--desktop__search__form__autocomplete__loader">Ladataan ehdotuksia...</em>' );
                this.displayAutocomplete();
            }
        );
    }

    /**
     * Handle keydown events.
     *
     * @param {object} evt The key push event.
     * @return {void}
     */
    keyDownHandler( evt ) {

        evt = evt || window.event;

        const KEY_TAB = 9;
        const KEY_ESC = 27;

        switch ( evt.keyCode ) {
            case KEY_TAB:

                // Return early if no hamburger found.
                if ( ! this.hamburger ) {
                    return;
                }

                // Focus on hamburger and return early if no other focusable elements found.
                if ( ! this.firstMobileMenuLink ) {
                    evt.preventDefault();
                    this.hamburger.focus();
                    break;
                }

                this.handleTabKeyEvent( evt );

                break;
            case KEY_ESC:
                if ( this.mobileOverlay.classList.contains( 'js-open' ) ) {
                    // Close the mobile menu.
                    this.toggleMobileOverlay();
                }

                // Check if there are any open menus.
                if( $('.menu-link.js-active').length ) {
                    // Close all the navigation menus.
                    this.closeNavigationMenus( evt );
                }

                break;
            default:
                break;
        } // end switch
    }

    /**
     * Handle keydown events when the search form is active.
     *
     * @param {object} evt The key push event.
     * @return {void}
     */
    searchOverlayKeyDownControl( evt ) {

        // Return early if modal not open.
        if ( ! this.darkOverlay.classList.contains( 'search-active' ) ) {
            return;
        }

        evt = evt || window.event;

        const KEY_TAB = 9;
        const KEY_ESC = 27;

        switch ( evt.keyCode ) {
            case KEY_TAB:

                this.searchInactive();

                break;
            case KEY_ESC:
                this.searchInactive();
                break;
            default:
                break;
        } // end switch
    }

    /**
     * Handle tab key pushes inside modal.
     *
     * @param {object} evt The key push event.
     * @return {void}
     */
    handleTabKeyEvent( evt ) {

        if ( ! this.firstMobileMenuLink || ! this.hamburger ) {
            return;
        }

        // If shift + tab pushed.
        if ( evt.shiftKey ) {

            // Focus on hamburger element if focus was on the first element.
            if ( document.activeElement === this.firstMobileMenuLink ) {
                evt.preventDefault();
                this.hamburger.focus();
                return;
            }

            // Focus on last element if focus was on the hamburger element.
            if ( document.activeElement === this.hamburger ) {
                evt.preventDefault();
                this.lastMobileMenuLink.focus();
                return;
            }
        } else {

            // Focus on hamburger element if focus was on the last element.
            if ( document.activeElement === this.lastMobileMenuLink ) {

                evt.preventDefault();
                this.hamburger.focus();
                return;
            }

            // Focus on first element if focus was on the hamburger element.
            if ( document.activeElement === this.hamburger ) {
                evt.preventDefault();
                this.firstMobileMenuLink.focus();
                return;
            }
        }
    }

    toggleMobileSearchBtnState() {
        if ( this.searchFormMobileInput.value.length >= 3 ) {
            this.searchFormMobileBtn.disabled = false;
        } else {
            this.searchFormMobileBtn.disabled = true;
        }
    }

    /**
     * Activate the search if input is changed and the searhc is inactive.
     *
     * @param {Event} evt JS event object.
     */
    maybeActivateSearch( evt ) {
        if ( ! this.darkOverlay.classList.contains( 'search-active' ) ) {
            this.searchActive( evt );
        }
    }

    /**
     * When search field is active.
     *
     * @param {Event} evt JS event object.
     */
    searchActive( evt ) {
        this.darkOverlay.classList.add( 'search-active' );
        evt.target.classList.add( 'js-active' );

        this.searchForm.classList.add( 'js-active' );

        // When search is opened, reset parent list items and
        // sub menus back to their default, closed states. Set the
        // aria-hidden state back to true when the menus are closed.
        this.closeAllNavMenus();

        // Also reset language menus if they exist.
        this.closeAllLangMenus();
    }

    /**
     * Make search state inactive.
     */
    searchInactive() {
        this.darkOverlay.classList.remove( 'search-active' );
        this.searchFormDesktopInput.classList.remove( 'js-active' );
        this.searchForm.classList.remove( 'js-active' );
        this.searchAutocompleteContainer.classList.remove( 'js-active' );
    }

    /**
     * Toggles the mobile menu overlay open and closed
     * @return {void}
     */
    toggleMobileOverlay() {

        // Return early if missing required elements.
        if ( ! this.hamburger ||  ! this.mobileOverlay ) {
            return;
        }

        // Get the toggler's aria-expanded current state and set a new opposite state to it.
        // Also set the opened container's aria-hidden state to the new value's opposite.
        const ariaExpandedState = this.hamburger.getAttribute( 'aria-expanded' ) === 'false' ? true : false;
        this.hamburger.setAttribute( 'aria-expanded', ariaExpandedState );
        this.mobileOverlay.setAttribute( 'aria-hidden', ! ariaExpandedState );

        // Toggle the overlay's and the hamburger's active states.
        this.mobileOverlay.classList.toggle( 'js-open' );
        this.hamburger.classList.toggle( 'is-active' );

        // Toggle the class on site body that prevents background-scrolling.
        document.body.classList.toggle( 'mobile-over-is-open' );

        // Return early if hamburger closed.
        if ( ! this.mobileOverlay.classList.contains( 'js-open' ) ) {
            this.hamburger.focus();
            return;
        }

        // Set a small delay to the focusing of mobile menu's first menu link
        // because of the CSS fade in.
        if ( this.firstMobileMenuLink ) {
            setTimeout( () => {
                this.firstMobileMenuLink.focus();
            }, 500 );
        }
    }

    /**
     * Toggles the sub menus open and closed
     * @return {void}
     */
    toggleSubMenu( e ) {

        let subMenu;

        // The clicked element
        let clickedElement  = e.target;

        // The link or button might contain an icon with the class 'sub-menu-indicator'. Check if the icon was clicked.
        if ( clickedElement.classList.contains( 'sub-menu-indicator' ) ) {
            clickedElement = e.target.parentNode;
        }
        else if ( clickedElement.classList.contains( 'menu-link-text' ) ) {
            clickedElement = e.target.parentNode;

            if ( ! clickedElement.classList.contains( 'menu-link-lvl-1' ) ) {
                window.location = clickedElement.href;
                return;
            }
        }

        // Get the parent elmenent of the clicked element and save it for later use.
        const clickedParent = clickedElement.parentNode;

        // Get the submenu inside the parent.
        subMenu = Theme.Common.sibling( clickedElement, 'sub-menu' );

        // Get the siblings of the clicked menu
        const siblingParents = Theme.Common.siblings( clickedParent, 'menu-item-has-children' );

        // Get the submenus of the siblings of the clicked menu
        const siblingSubMenus = Theme.Common.childrenFromEach( siblingParents, '.sub-menu' );

        // Toggle the clicked parent's active state and its sub menu's active state.
        clickedElement.classList.toggle( 'js-active' );
        if ( subMenu ) {
            subMenu.classList.toggle( 'js-active' );

            // Toggle the clicked parent's sub menu's aria-hidden state.
            Theme.Common.toggleAriaHidden( subMenu );
        }

        // Remove the sibling parents' active states and their sub menus' active states.
        // Set the aria-hidden state to true when the sub menus are closed.
        Theme.Common.removeClassFromAll( siblingParents, 'js-active' );
        Theme.Common.removeClassFromAll( siblingSubMenus, 'js-active' );
        Theme.Common.setAttrOnAll( siblingSubMenus, 'aria-hidden', true );

        // Close any open nav-menus
        this.closeAllLangMenus();
    }

    /**
     * Toggles the sub menus open and closed
     * @return {void}
     */
    toggleLangMenu( e ) {

        // The clicked element
        let clickedElement  = e.target;

        // The link might contain an icon with the class 'sub-menu-indicator'. Check if the icon was clicked.
        if ( clickedElement.classList.contains( 'sub-menu-indicator' ) ) {
            clickedElement = e.target.parentNode;
        }

        // Get the parent element of the clicked element.
        const clickedParent = clickedElement.parentNode;

        // Get the submenu inside the parent.
        const subMenu = Theme.Common.$1( '.sub-menu', clickedParent );

        // When search is opened, reset parent list items and
        // sub menus back to their default, closed states. Set the
        // aria-hidden state back to true when the menus are closed.
        this.closeAllNavMenus();

        // Toggle the clicked parent's active state and its sub menu's active state.
        clickedParent.classList.toggle( 'js-active' );
        if ( subMenu ) {
            subMenu.classList.toggle( 'js-active' );

            // Toggle the clicked parent's sub menu's aria-hidden state.
            Theme.Common.toggleAriaHidden( subMenu );
        }
    }

    /**
     * Resets all open main menus
     */
    closeAllNavMenus() {
        if ( this.menuParents && this.menuSubMenus ) {
            Theme.Common.removeClassFromAll( this.menuParents, 'js-active' );
            Theme.Common.removeClassFromAll( this.menuSubMenus, 'js-active' );
            Theme.Common.setAttrOnAll( this.menuSubMenus, 'aria-hidden', true );
        }
    }

    /**
     * Resets all open language menus
     */
    closeAllLangMenus() {
        if ( this.langMenuParents && this.langMenuSubMenus ) {
            Theme.Common.removeClassFromAll( this.langMenuParents, 'js-active' );
            Theme.Common.removeClassFromAll( this.langMenuSubMenus, 'js-active' );
            Theme.Common.setAttrOnAll( this.langMenuSubMenus, 'aria-hidden', true );
        }
    }

    /**
     * Close open navigation menus.
     */
     closeNavigationMenus( event ) {

        if( event instanceof MouseEvent ) {
            // Allow normal use of the menu.
            if( $(event.target).is( '.sub-menu-btn, .sub-menu-indicator, .menu-link-text' ) ) {
                return;
            }
        } 

        // Close the menu.
        $( '.menu-link.js-active' ).removeClass( 'js-active' );
        $( '.sub-menu.js-active' ).removeClass( 'js-active' );
    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.cache();
        this.events();
        this.searchAutocomplete();
    }
}
